import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, SelectsOptions, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfIntermediaryDraftDto,
  IntermediaryDraftDataRow,
  intermediaryGetById,
  intermediaryCreateChangeRequest,
  IntermediaryResponse,
} from '@pmp/api';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';
import { CrudFormProps } from '@pmp/types';
import { mapIntermediaryToDto } from '..';
import { BrokersFormSchema } from '.';

export const BrokersCrudForm = memo(
  ({ mode: modeProp, record, valueLists, refreshTable, onClose }: CrudFormProps<IntermediaryResponse>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<CrudMode | null>(modeProp);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    useEffect(() => {
      if (mode === CrudMode.EDIT && !record?.id) {
        notification().error({ message: t('global.errors.unexpected') });
        onClose();
        return;
      }
      setOpen(true);
    }, [mode, record, t, onClose]);

    const request = useCallback(async () => {
      if (!open || mode === CrudMode.CREATE || !record?.id) return;
      setLoading(true);
      intermediaryGetById({ path: { id: record.id } })
        .then(({ data }) => {
          if (!data) return;
          const formValues = {
            ...data,
            personalData: data.personalData
              ? {
                ...data.personalData,
                birthDate: data.personalData.birthDate ? dayjs(data.personalData.birthDate) : null,
              }
              : data.personalData,
            addedToPanel: data.addedToPanel ? dayjs(data.addedToPanel) : null,
            lastUpdate: data.lastUpdate ? dayjs(data.lastUpdate) : null,
          };
          ref.current?.form?.setFieldsValue(formValues);
        })
        .catch(() => {
          notification().error({ message: t('global.errors.unexpected') });
        })
        .finally(() => setLoading(false));
    }, [open, mode, record?.id, t]);

    useEffect(() => {
      request();
    }, [request]);

    const mapFormValuesToDraft = useCallback(
      (values: IntermediaryResponse): IntermediaryDraftDataRow => mapIntermediaryToDto(values, record),
      [record],
    );

    const onConfirm: (formData: IntermediaryResponse) => Promise<ValidationError[] | void> = useCallback(
      (values: IntermediaryResponse) => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const data = mapFormValuesToDraft(values);
        const body: ChangeRequestOfIntermediaryDraftDto = {
          data,
          actionType: crudToActionMap[mode],
        };

        return intermediaryCreateChangeRequest({ body }).then(
          ({ data: responseData }) => {
            if (!responseData.isValid) {
              return transformCrudFormErrors({ ...responseData.errors });
            }
            if (mode === CrudMode.EDIT) refreshTable();
            notification().success({
              message: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes, mapFormValuesToDraft],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.brokers.labels.broker') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('lender.brokers.labels.broker') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('lender.brokers.labels.broker') }),
      };
      return titles[mode];
    }, [mode, t]);

    const selectsOptions: SelectsOptions = useMemo(() => (valueLists && getSelectsOptions(valueLists)) || {}, [valueLists]);

    const crudProps: CrudProps<IntermediaryResponse> = useMemo(() => {
      return {
        ref,
        open,
        loading,
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        mode,
        customForm: true,
        width: 1100,
        wrapperCol: { flex: 'auto' },
        disableDrawerBodyScroll: true,
        onClose: handleOnClose,
        onConfirm,
      };
    }, [open, loading, title, mode, t, handleOnClose, onConfirm, enableFourEyes]);

    return (
      <Crud {...crudProps}>
        <BrokersFormSchema form={ref.current?.form} mode={mode ?? CrudMode.VIEW} selectsOptions={selectsOptions} />
      </Crud>
    );
  },
);
